import { Modal, Box } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import APISearchInput from "../../../search/APISearchInput";
import CustomPagination from "../../../table/CustomPagination";
import CustomStyledTable from "../../../table/CustomStyledTable";
import TableFilter from "../../../table/filter/TableFilter";
import { ModalStyle } from "../../../../helpers/appHelpers";
import { useManageTableWithStateVariables } from "../../../../helpers/useManageTable";
import { AuditLogDto, AuditLogOrderBy } from "../../../../models/audit-log.dto";
import { useGetAuditLogSearch, useGetAuditLog } from "../../../../services/audit-log.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useUserStore from "../../../../state-management/useUserStore";
import { GridColDef } from "@mui/x-data-grid";
import { useEffect, useState } from "react";
import { AuditLogColumns } from "./data";

type OrgAuditLogsProps = {
	openModal: boolean;
	setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrgAuditLogs = ({
	openModal,
	setOpenModal,
}: OrgAuditLogsProps) => {
	const { currentUser, userType } = useUserStore();
    const { savedCompanies } = useCompanyStore();
	const [tableKey, setTableKey] = useState(Math.random());
	const [companyName, setCompanyName] = useState("");
	
	const { mutate: searchAuditLogs } = useGetAuditLogSearch();
	const {
        tableAPIRef,
        defaultTableData,
        tableData,
		tableFilter,
        isLoading,
        page,
        pageCount,
		setTableData,
		setTableFilter,
		setSearching,
        handlePageChange,
	} = useManageTableWithStateVariables<AuditLogDto>({ 
		fetchHook: useGetAuditLog,
		options: { 
			organizationId: currentUser.user!.organizationId,
			orderBy: AuditLogOrderBy.TIMESTAMP_DESC,
		}
	});

	const columnsWithCompany: GridColDef[] = [
		{
			field: "company",
			headerName: "Company",
			minWidth: 280,
			flex: 1,
			headerClassName: "super-app-theme--header",
			valueGetter: (params) => params.value?.name || "--",
		},
		...AuditLogColumns
	]
	const [tableColumns, setTableColumns] = useState<GridColDef[]>(
		userType === "ORGANIZATION" ? columnsWithCompany : AuditLogColumns
	);

	useEffect(() => {
		if (userType === "ADMIN") return;

		if (tableFilter?.companyId) {
			setTableColumns(AuditLogColumns);
			setTableKey(Math.random());
			setCompanyName(
				savedCompanies.find((company) => company.id === Number(tableFilter.companyId))?.name || ""
			);
			return
		}

		setCompanyName("");
		setTableColumns(columnsWithCompany);
	}, [tableFilter])

	return (
		<Modal
			open={openModal}
			aria-labelledby="modal-modal-title"
			aria-describedby="modal-modal-description"
		>
			<Box sx={ModalStyle} width={1260} fontSize={"13px"}>
				<div className="flex justify-between items-center pt-4 pb-6">
					<div className="flex items-center gap-5">
						<h1 className="font-bold text-lg">Audit Logs</h1>
						<div className="flex items-center gap-3">
							<APISearchInput 
								fallbackData={defaultTableData}
								setTableData={setTableData}
								mutate={searchAuditLogs}
								setSearching={setSearching}
								popupTable
							/>
							<TableFilter
								fields={
									userType === "ORGANIZATION" 
										? [
											{
												title: "",
												fieldName: "",
												fieldType: "SORT",
												options: [
													{
														title: "Timestamp",
														fieldName: "timestamp",
													},
												]
											},
											{
												title: "Company",
												fieldName: "companyId",
												fieldType: "SELECT_FIELD",
												options: savedCompanies,
											},
										]
										: [
											{
												title: "",
												fieldName: "",
												fieldType: "SORT",
												options: [
													{
														title: "Timestamp",
														fieldName: "timestamp",
													},
												]
											},
										]
								}
								externalFilter={tableFilter}
								setExternalFilter={setTableFilter}
								setSearching={setSearching}
							/>
							{companyName && (
								<div className="bg-[#6978F0] font-semibold text-white whitespace-nowrap 
									text-xs px-2 py-[6px] rounded flex items-center justify-center"
								>
									{companyName}
								</div>
							)}
						</div>
					</div>
					<button onClick={() => setOpenModal(false)}>
						<IoMdClose className="text-xl hover:scale-[1.1]" />
					</button>
				</div>
				<hr className="mb-6" />
				<CustomStyledTable
					key={tableKey}
					rows={tableData}
					columns={tableColumns}
					apiRef={tableAPIRef}
					loading={isLoading}
					pageCount={pageCount}
					pagination={
						<CustomPagination 
							pageCount={pageCount} 
							pageState={page}
							handleChange={handlePageChange}
						/>
					}
				/>
			</Box>
		</Modal>
	);
};
 
export default OrgAuditLogs;